import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { adminApi } from '@/features/admin/services/adminApi';
import { getErrorMessage, getErrorStatus } from '@/shared/services/api';
import {
  SERVER_LOG_LEVELS,
  type ServerLogEntry,
  type ServerLogLevel,
  type ServerLogQuery,
  type ServerLogScan,
  type ServerLogSource,
} from '@/shared/types/api';

export type LogWindow = '15m' | '1h' | '24h' | '7d' | 'all';

export const LOG_WINDOWS: { id: LogWindow; label: string; ms?: number }[] = [
  { id: '15m', label: 'Last 15 minutes', ms: 15 * 60_000 },
  { id: '1h', label: 'Last hour', ms: 60 * 60_000 },
  { id: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60_000 },
  { id: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60_000 },
  { id: 'all', label: 'All retained' },
];

const DEFAULT_LEVEL: ServerLogLevel = 'Warning';
const DEFAULT_WINDOW: LogWindow = '24h';
export const FILTER_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;
const BUSY_MESSAGE = 'Log viewer is busy — try again in a moment.';

/** The Logs filters, as they live in the URL (?tab=logs&minLevel=…&q=…&window=…). */
export interface LogFilters {
  minLevel: ServerLogLevel;
  q: string;
  window: LogWindow;
  from?: string;
  to?: string;
  traceId?: string;
  source: string;
}

/** URL param per filter key; `q` stays short so shared links stay readable. */
export type LogFilterPatch = Partial<Record<keyof LogFilters, string | undefined>>;

const isLevel = (v: string | null): v is ServerLogLevel => SERVER_LOG_LEVELS.includes(v as ServerLogLevel);
const isWindow = (v: string | null): v is LogWindow => LOG_WINDOWS.some((w) => w.id === v);

function readFilters(params: URLSearchParams): LogFilters {
  const traceId = params.get('traceId') || undefined;
  const from = params.get('from') || undefined;
  const requestedWindow = params.get('window');
  // A trace spans seconds and may be days old: with no explicit start, search all retained files.
  const window = isWindow(requestedWindow) ? requestedWindow : traceId && !from ? 'all' : DEFAULT_WINDOW;
  const requestedLevel = params.get('minLevel');
  return {
    // Every line of a request tells its story, so a trace always shows all levels.
    minLevel: traceId ? 'Verbose' : isLevel(requestedLevel) ? requestedLevel : DEFAULT_LEVEL,
    q: params.get('q') ?? '',
    window,
    from,
    to: params.get('to') || undefined,
    traceId,
    source: params.get('source') ?? '',
  };
}

/** Builds the request for the first page. An explicit `from` overrides the relative window. */
function toQuery(f: LogFilters): ServerLogQuery {
  const windowMs = LOG_WINDOWS.find((w) => w.id === f.window)?.ms;
  const from = f.from ?? (windowMs ? new Date(Date.now() - windowMs).toISOString() : undefined);
  return {
    minLevel: f.minLevel,
    search: f.q.trim() || undefined,
    from,
    to: f.to,
    traceId: f.traceId,
    source: f.source.trim() || undefined,
    limit: PAGE_SIZE,
  };
}

const isCancel = (e: unknown) => axios.isCancel(e) || (e instanceof DOMException && e.name === 'AbortError');

/**
 * Server-log state for the Logs tab. Filters come from the URL; the first load is immediate and
 * filter changes are debounced. Each request has its own AbortController, and a generation
 * counter (as in feedStore) drops a slow earlier response instead of letting it overwrite a
 * newer one. A refresh supersedes a pending "Load older" (and clears its spinner); "Load older"
 * never starts while a refresh is in flight, since it would continue the previous query. No
 * polling in phase 1 — Refresh is explicit.
 */
export function useServerLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const filterKey = JSON.stringify(filters);

  const [items, setItems] = useState<ServerLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [scan, setScan] = useState<ServerLogScan | null>(null);
  const [source, setSource] = useState<ServerLogSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const refreshing = useRef(false);
  // The query the current list was built from, so "Load older" continues the same window, and
  // the filters that query came from, so a failed refresh can tell whether the list is stale.
  const lastQuery = useRef<ServerLogQuery | null>(null);
  const lastFilterKey = useRef<string | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const reportError = (e: unknown): string => {
    const status = getErrorStatus(e);
    const message = status === 429 || status === 503 ? BUSY_MESSAGE : getErrorMessage(e, 'Failed to load server logs');
    toast.error(message);
    return message;
  };

  const begin = () => {
    controller.current?.abort();
    const ac = new AbortController();
    controller.current = ac;
    return { gen: ++generation.current, ac };
  };

  const refresh = useCallback(async () => {
    const { gen, ac } = begin();
    const key = JSON.stringify(filtersRef.current);
    const query = toQuery(filtersRef.current);
    refreshing.current = true;
    setLoading(true);
    // Any pending "Load older" was just aborted; its own finally skips the reset as superseded.
    setLoadingMore(false);
    try {
      const page = await adminApi.getServerLogs(query, ac.signal);
      if (gen !== generation.current) return;
      lastQuery.current = query;
      lastFilterKey.current = key;
      setItems(page.items);
      setNextCursor(page.nextCursor ?? null);
      setScan(page.scan);
      setSource(page.source);
      setError(null);
    } catch (e) {
      if (gen !== generation.current || isCancel(e)) return;
      setError(reportError(e));
      // The rows on screen belong to other filters: drop them rather than show them under the
      // new controls, and make sure "Load older" cannot continue that old query.
      if (lastFilterKey.current !== key) {
        lastQuery.current = null;
        lastFilterKey.current = null;
        setItems([]);
        setNextCursor(null);
      }
    } finally {
      if (gen === generation.current) {
        refreshing.current = false;
        setLoading(false);
      }
    }
  }, []);

  const loadOlder = useCallback(async () => {
    if (!nextCursor || !lastQuery.current || refreshing.current) return;
    const { gen, ac } = begin();
    const query = { ...lastQuery.current, cursor: nextCursor };
    setLoadingMore(true);
    try {
      const page = await adminApi.getServerLogs(query, ac.signal);
      if (gen !== generation.current) return;
      setItems((prev) => {
        // A cursor resumes after the last line returned, but guard against a repeat anyway.
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...page.items.filter((i) => !seen.has(i.id))];
      });
      setNextCursor(page.nextCursor ?? null);
      setScan(page.scan);
      setSource(page.source);
    } catch (e) {
      if (gen !== generation.current || isCancel(e)) return;
      reportError(e);
    } finally {
      if (gen === generation.current) setLoadingMore(false);
    }
  }, [nextCursor]);

  // First load immediately; later filter changes wait until typing settles.
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (!loadedOnce.current) {
      loadedOnce.current = true;
      void refresh();
      return;
    }
    const timer = setTimeout(() => void refresh(), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filterKey, refresh]);

  // Unmounting aborts the in-flight read, so the next mount must load immediately again. (Under
  // StrictMode's dev remount the aborted first load would otherwise wait out the debounce, showing
  // "No log entries match" meanwhile.)
  useEffect(
    () => () => {
      controller.current?.abort();
      loadedOnce.current = false;
    },
    []
  );

  /** Merges `patch` into the URL (undefined or '' removes a key), keeping ?tab and other params. */
  const updateFilters = useCallback(
    (patch: LogFilterPatch) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  return { filters, updateFilters, items, nextCursor, scan, source, loading, loadingMore, error, refresh, loadOlder };
}
